import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Globe } from "lucide-react";
import { content, whatsappUrl } from "@/lib/content";
import { useLanguage } from "@/lib/language";
import { switchLangPath } from "@/lib/site";
import { LangLink } from "@/components/LangLink";
import { cn } from "@/lib/utils";

export function Navbar() {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const otherLangHref = switchLangPath(pathname);

  const links = [
    { label: content.nav.home, to: "/" },
    { label: content.nav.about, to: "/about" },
    { label: content.nav.services, to: "/services" },
    { label: content.nav.industries, to: "/industries" },
    { label: content.nav.insights, to: "/insights" },
    { label: content.nav.contact, to: "/contact" },
  ];

  return (
    <header
      className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md"
      onBlurCapture={(event) => {
        if (
          event.relatedTarget instanceof Node &&
          !event.currentTarget.contains(event.relatedTarget)
        )
          setOpen(false);
      }}
      onKeyDown={(event) => {
        if (open && event.key === "Escape") {
          setOpen(false);
          event.currentTarget
            .querySelector<HTMLButtonElement>('button[aria-controls="mobile-navigation"]')
            ?.focus();
        }
      }}
    >
      <div className="mx-auto flex max-w-7xl w-full items-center justify-between px-6 sm:px-8 py-5">
        <LangLink to="/" className="text-2xl font-extrabold tracking-tight text-foreground">
          Iraq<span className="text-primary">Cert</span>
        </LangLink>

        <nav className="hidden items-center gap-8 xl:flex">
          {links.map((link) => (
            <LangLink
              key={link.label.en}
              to={link.to}
              className="text-sm font-medium text-foreground/80 transition-colors hover:text-primary"
              activeProps={{ className: "text-primary" }}
              activeOptions={{ exact: link.to === "/" }}
            >
              {t(link.label)}
            </LangLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-cta="persistent-header-whatsapp"
            aria-label={lang === "ar" ? "تواصل معنا عبر واتساب" : "Talk to us on WhatsApp"}
            className="hidden min-h-11 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary xl:inline-flex"
          >
            {lang === "ar" ? "واتساب" : "WhatsApp"}
          </a>
          <Link
            to={otherLangHref as never}
            search={true}
            hash={true}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-semibold transition-colors hover:border-primary hover:text-primary"
            aria-label={lang === "en" ? "التبديل إلى العربية" : "Switch to English"}
          >
            <Globe className="size-4" />
            <span className={cn(lang === "en" && "text-primary")}>EN</span>
            <span className="text-muted-foreground">/</span>
            <span className={cn(lang === "ar" && "text-primary")}>AR</span>
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-2 xl:hidden"
            aria-label={lang === "en" ? "Toggle menu" : "فتح القائمة أو إغلاقها"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      <div className="flex justify-center xl:hidden border-t border-border bg-[#f5f3ef] px-4 py-1.5">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-cta="persistent-header-whatsapp"
          className="inline-flex min-h-11 items-center justify-center rounded-md px-5 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {lang === "ar" ? "تواصل معنا عبر واتساب" : "Talk to us on WhatsApp"}
        </a>
      </div>
      {open && (
        <nav
          id="mobile-navigation"
          className="flex flex-col gap-1 border-t border-border bg-background px-4 py-3 xl:hidden"
        >
          {links.map((link) => (
            <LangLink
              key={link.label.en}
              to={link.to}
              onClick={() => setOpen(false)}
              className="flex min-h-11 items-center rounded-md px-3 py-2 text-start text-sm font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-primary"
              activeProps={{ className: "text-primary" }}
              activeOptions={{ exact: link.to === "/" }}
            >
              {t(link.label)}
            </LangLink>
          ))}
        </nav>
      )}
    </header>
  );
}
